import {
  REVIEW_FILTERS,
  claimFails,
  matchesFilter,
  type Provenance,
  type ReviewFilter,
  type ReviewRow,
  type ReviewState,
} from '@cyberscore/contracts';

export const STATE_TONE: Record<ReviewState, 'ok' | 'bad' | 'warn' | 'neutral'> = {
  to_review: 'neutral',
  changed: 'warn',
  approved: 'ok',
  flagged: 'bad',
  change_requested: 'warn',
  unanswered: 'neutral',
};

export const PROVENANCE_LABEL: Record<Provenance, string> & Partial<Record<string, string>> = {
  USER_DECLARED: 'Said by the business',
  AI_EXTRACTED: 'Read from a document',
  DOCUMENT_VERIFIED: 'Checked against a document',
  API_DISCOVERED: 'From a connected system',
  SECURITY_SCAN: 'From a security scan',
  ADMIN_ENTERED: 'Entered by staff',
  EXTERNAL_PROVIDER: 'From an outside provider',
  INFERRED: 'Inferred',
};

/** How many rows each filter would show. */
export function filterCounts(rows: readonly ReviewRow[]): Record<ReviewFilter, number> {
  return Object.fromEntries(REVIEW_FILTERS.map((f) => [f, rows.filter((r) => matchesFilter(f, r)).length])) as Record<ReviewFilter, number>; // prettier-ignore
}

/** Rows a reviewer can approve in one go: answered, waiting for a review, failing no control or check. */
export const bulkApprovable = (rows: readonly ReviewRow[]) =>
  rows.filter(
    (r) =>
      r.answer &&
      (r.state === 'to_review' || r.state === 'changed') &&
      r.failing.length === 0 &&
      !(r.checks && claimFails(r.checks)),
  );

/**
 * The rows in review order (task 110, decision 0074): the ones that need a person's judgement first, the
 * clean ones (approvable in one go) after them, each group in its own order.
 */
export function exceptionsFirst<T extends ReviewRow>(rows: readonly T[]): T[] {
  const clean = new Set(bulkApprovable(rows));
  return [...rows.filter((r) => !clean.has(r)), ...rows.filter((r) => clean.has(r))];
}
